import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import type { Sowing } from "../../services/sowingsApi";

interface Props {
  sowing: Sowing;
}

const SEED_SOURCE_LABEL: Record<number, string> = {
  1: "Company seed",
  2: "Customer seed",
};

const SEED_SOURCE_VARIANT: Record<number, "default" | "secondary" | "outline"> = {
  1: "secondary",
  2: "outline",
};

const formatDate = (iso?: string | null) => {
  if (!iso) return "—";
  try {
    return format(new Date(iso), "yyyy-MM-dd");
  } catch {
    return iso;
  }
};

/// Read-only summary of the parent SowingInstruction. Spec §"Amendment UI — context block".
export const SowingContextBlock = ({ sowing }: Props) => {
  const seedLabel =
    sowing.seedSource != null
      ? SEED_SOURCE_LABEL[sowing.seedSource] ?? `Source ${sowing.seedSource}`
      : null;

  return (
    <div className="rounded-md border bg-muted/40 p-3 space-y-2 text-sm">
      <div className="flex items-center justify-between gap-2">
        <div className="font-medium">
          {sowing.instructionNumber ?? "Instruction"}
          {sowing.cropTypeName && ` · ${sowing.cropTypeName}`}
          {sowing.variety && ` · ${sowing.variety}`}
        </div>
        {seedLabel && (
          <Badge variant={SEED_SOURCE_VARIANT[sowing.seedSource!] ?? "outline"}>
            {seedLabel}
            {sowing.seedBatchNumber && ` · ${sowing.seedBatchNumber}`}
          </Badge>
        )}
      </div>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground">
        {(sowing.scheduledStartDate || sowing.scheduledEndDate) && (
          <>
            <dt>Sowing window</dt>
            <dd>
              {formatDate(sowing.scheduledStartDate)}
              {sowing.scheduledEndDate &&
                sowing.scheduledEndDate !== sowing.scheduledStartDate &&
                ` → ${formatDate(sowing.scheduledEndDate)}`}
            </dd>
          </>
        )}
        {sowing.orderNumber && (
          <>
            <dt>Order</dt>
            <dd>{sowing.orderNumber}</dd>
          </>
        )}
        {sowing.customerName && (
          <>
            <dt>Customer</dt>
            <dd>{sowing.customerName}</dd>
          </>
        )}
        {sowing.requisitionNumber && (
          <>
            <dt>Requisition</dt>
            <dd>{sowing.requisitionNumber}</dd>
          </>
        )}
      </dl>
    </div>
  );
};
